import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Check, Layers, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCart } from "@/contexts/CartContext";
import { PIZZA_CRUSTS, PIZZA_TOPPINGS as DEFAULT_TOPPINGS, formatPrice } from "@/lib/menuData";
import { PIZZA_TOPPING_IMAGES } from "@/lib/foodImages";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Specialty pizza price is always $12.99
const SPECIALTY_PRICE = 12.99;

// Phase 15.4: Default topping presets by specialty pizza id.
// Presets auto-apply to the "whole" pizza AND to each half when the user picks a specialty
// on either side of a Half & Half. Users can freely deselect any pre-picked topping.
// Jalapeños are intentionally NOT in any preset (customer opt-in only).
const SPECIALTY_PRESETS = {
  pz_meat: ["Pepperoni", "Sausage", "Bacon", "Beef"],
  pz_veggie: ["Green Peppers", "Mushrooms", "Onions", "Black Olives"],
  pz_loaded: ["Pepperoni", "Sausage", "Beef", "Green Peppers", "Mushrooms", "Onions", "Black Olives"],
  pz_hawaiian: ["Ham", "Pineapple"],
  pz_hamburger: ["Beef", "Onions"],
};

const getPresetFor = (id) => (id && SPECIALTY_PRESETS[id]) || [];

// Topping checklist component (defined outside to avoid render issues)
function ToppingChecklist({ toppingsData, selected, onToggle, side }) {
  return (
    <div className="space-y-1">
      <h5 className="font-bold text-xs text-slate-900 mt-2 mb-1" style={{ fontFamily: "Manrope" }}>Meats</h5>
      {(toppingsData.meats || []).map((t) => {
        const name = typeof t === 'string' ? t : t.name;
        const imgUrl = PIZZA_TOPPING_IMAGES[name];
        return (
          <label key={name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
            {imgUrl ? (
              <img src={imgUrl} alt={name} className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 border-slate-200" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <div className="w-9 h-9 rounded-full bg-slate-100 flex-shrink-0 border-2 border-slate-200" />
            )}
            <Checkbox checked={selected.includes(name)} onCheckedChange={() => onToggle(name, side)} />
            <span className="text-sm font-medium text-slate-700">{name}</span>
          </label>
        );
      })}
      
      <h5 className="font-bold text-xs text-slate-900 mt-3 mb-1" style={{ fontFamily: "Manrope" }}>Veggies</h5>
      {(toppingsData.veggies || []).map((t) => {
        const name = typeof t === 'string' ? t : t.name;
        const imgUrl = PIZZA_TOPPING_IMAGES[name];
        return (
          <label key={name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
            {imgUrl ? (
              <img src={imgUrl} alt={name} className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 border-slate-200" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <div className="w-9 h-9 rounded-full bg-slate-100 flex-shrink-0 border-2 border-slate-200" />
            )}
            <Checkbox checked={selected.includes(name)} onCheckedChange={() => onToggle(name, side)} />
            <span className="text-sm font-medium text-slate-700">{name}</span>
          </label>
        );
      })}
      
      {(toppingsData.premium || []).length > 0 && (
        <>
          <h5 className="font-bold text-xs mt-3 mb-1" style={{ fontFamily: "Manrope", color: "#B45309" }}>
            Premium Toppings
          </h5>
          {(toppingsData.premium || []).map((t) => {
            const name = typeof t === 'string' ? t : t.name;
            const extraPrice = t.extra_price || 0;
            const imgUrl = PIZZA_TOPPING_IMAGES[name];
            return (
              <label key={name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-amber-50 cursor-pointer border border-amber-200">
                {imgUrl ? (
                  <img src={imgUrl} alt={name} className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 border-amber-300" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex-shrink-0 border-2 border-amber-300" />
                )}
                <Checkbox checked={selected.includes(name)} onCheckedChange={() => onToggle(name, side)} />
                <span className="text-sm font-medium text-slate-700">{name}</span>
                <span className="ml-auto text-xs font-bold" style={{ color: "#B45309" }}>
                  +${extraPrice.toFixed(2)}
                </span>
              </label>
            );
          })}
        </>
      )}
    </div>
  );
}

export default function PizzaBuilder({ open, onClose, pizzaItem, menuItems }) {
  const isSpecialty = pizzaItem?.isSpecialty || false;
  const isLimitedEdition = pizzaItem?.isLimitedEdition || false;
  // Specialty pizzas always $12.99
  const basePrice = isSpecialty ? SPECIALTY_PRICE : (pizzaItem?.price || 11.99);

  const [selectedCrust, setSelectedCrust] = useState(null);
  const [halfAndHalf, setHalfAndHalf] = useState(false);
  const [leftToppings, setLeftToppings] = useState([]);
  const [rightToppings, setRightToppings] = useState([]);
  const [wholeToppings, setWholeToppings] = useState([]);
  const [extraCheese, setExtraCheese] = useState(false);
  const [toppings, setToppings] = useState({ meats: [], veggies: [], premium: [] });
  
  // Crust availability state
  const [crustAvailability, setCrustAvailability] = useState({ regular: true, thin: true });
  const [loadingCrust, setLoadingCrust] = useState(true);
  
  const { addItem } = useCart();

  // Fetch crust availability on open
  useEffect(() => {
    const fetchCrustAvailability = async () => {
      try {
        const res = await axios.get(`${API}/settings/crust-availability`);
        setCrustAvailability(res.data);
      } catch (e) {
        console.error("Failed to fetch crust availability, defaulting to all available");
        setCrustAvailability({ regular: true, thin: true });
      } finally {
        setLoadingCrust(false);
      }
    };
    if (open) {
      setLoadingCrust(true);
      fetchCrustAvailability();
    }
  }, [open]);

  // Fetch toppings from API
  useEffect(() => {
    const fetchToppings = async () => {
      try {
        const res = await axios.get(`${API}/inventory/pizza-toppings`);
        if (res.data) {
          setToppings(res.data);
        }
      } catch (e) {
        console.error("Failed to fetch toppings, using defaults");
        // Fallback to defaults
        setToppings({
          meats: DEFAULT_TOPPINGS.meats.map(name => ({ name, extra_price: 0 })),
          veggies: DEFAULT_TOPPINGS.veggies.map(name => ({ name, extra_price: 0 })),
          premium: []
        });
      }
    };
    fetchToppings();
  }, []);

  // Filter available crusts
  const availableCrusts = useMemo(() => {
    if (isSpecialty) {
      // Phase 14: Hawaiian Pizza is a specialty but flagged `allows_thin_crust`,
      // so it should offer both crusts (subject to the admin availability toggle).
      // All other specialty pizzas remain Regular Crust only.
      const allowsThin = !!pizzaItem?.allows_thin_crust;
      return PIZZA_CRUSTS.filter(c => {
        if (c.id === "regular") return crustAvailability.regular;
        if (c.id === "thin") return allowsThin && crustAvailability.thin;
        return false;
      });
    }
    return PIZZA_CRUSTS.filter(c => {
      if (c.id === "regular") return crustAvailability.regular;
      if (c.id === "thin") return crustAvailability.thin;
      return true;
    });
  }, [isSpecialty, crustAvailability, pizzaItem]);

  // Auto-select crust if only one is available (or if the pizza has a preset — treat it like a specialty)
  useEffect(() => {
    if (!loadingCrust && open && pizzaItem) {
      const pizzaHasPreset = !!SPECIALTY_PRESETS[pizzaItem.id];
      if (availableCrusts.length === 1) {
        setSelectedCrust(availableCrusts[0].name);
      } else if ((isSpecialty || pizzaHasPreset) && crustAvailability.regular) {
        setSelectedCrust("Regular Crust");
      } else {
        setSelectedCrust(null);
      }
    }
  }, [loadingCrust, open, pizzaItem, availableCrusts, isSpecialty, crustAvailability]);

  // Reset state when pizzaItem changes; auto-populate specialty topping presets
  useEffect(() => {
    if (open && pizzaItem) {
      setHalfAndHalf(false);
      setLeftToppings([]);
      setRightToppings([]);
      // Preset lookup is keyed purely off pizzaItem.id so Meat Lovers / Veggie / Loaded
      // still auto-populate even when they're catalogued under `regular_pizza`.
      const preset = getPresetFor(pizzaItem.id);
      setWholeToppings(preset);
      setExtraCheese(false);
    }
  }, [open, pizzaItem]);

  // The specialty-preset hint label + Ham synthesis apply whenever the current pizza has a
  // known preset — not only for isSpecialty items.
  const hasPreset = !!(pizzaItem && SPECIALTY_PRESETS[pizzaItem.id]);

  // Expose a synthetic "Ham" veggie/meat if the current specialty presets need Ham but the
  // backend inventory doesn't ship it (Ham was previously removed). Injected as an extra meat.
  const displayToppings = useMemo(() => {
    const preset = getPresetFor(pizzaItem?.id);
    const meatNames = new Set((toppings.meats || []).map((t) => (typeof t === "string" ? t : t.name)));
    const veggieNames = new Set((toppings.veggies || []).map((t) => (typeof t === "string" ? t : t.name)));
    const premiumNames = new Set((toppings.premium || []).map((t) => (typeof t === "string" ? t : t.name)));
    const extraMeats = preset
      .filter((n) => !meatNames.has(n) && !veggieNames.has(n) && !premiumNames.has(n))
      .map((name) => ({ name, extra_price: 0 }));
    if (extraMeats.length === 0) return toppings;
    return { ...toppings, meats: [...(toppings.meats || []), ...extraMeats] };
  }, [toppings, pizzaItem]);

  const extraCheesePrice = useMemo(() => {
    const addon = (menuItems || []).find((i) => i.id === "pz_extra_cheese");
    return addon ? addon.price : 2.49;
  }, [menuItems]);

  // Calculate premium topping costs
  const getPremiumToppingCost = (selectedList) => {
    const allPremium = toppings.premium || [];
    return selectedList.reduce((sum, name) => {
      const found = allPremium.find(p => (typeof p === 'string' ? p : p.name) === name);
      return sum + (found && typeof found !== 'string' ? (found.extra_price || 0) : 0);
    }, 0);
  };

  const premiumCostWhole = getPremiumToppingCost(wholeToppings);
  const premiumCostLeft = getPremiumToppingCost(leftToppings);
  const premiumCostRight = getPremiumToppingCost(rightToppings);
  const totalPremiumCost = halfAndHalf 
    ? premiumCostLeft + premiumCostRight 
    : premiumCostWhole;

  const totalPrice = basePrice + (extraCheese ? extraCheesePrice : 0) + totalPremiumCost;

  const reset = () => {
    setSelectedCrust(null);
    setHalfAndHalf(false);
    setLeftToppings([]);
    setRightToppings([]);
    setWholeToppings([]);
    setExtraCheese(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const toggleTopping = (topping, side) => {
    const setter = side === "left" ? setLeftToppings : side === "right" ? setRightToppings : setWholeToppings;
    setter((prev) => prev.includes(topping) ? prev.filter((t) => t !== topping) : [...prev, topping]);
  };

  const handleConfirm = () => {
    if (!selectedCrust) return;
    const customizations = { crust: selectedCrust };
    if (extraCheese) customizations.extra_cheese = `Yes (+$${extraCheesePrice.toFixed(2)})`;
    if (isLimitedEdition) customizations.isLimitedEdition = true;
    if (isSpecialty) customizations.isSpecialty = true;

    if (halfAndHalf) {
      customizations.left_half = leftToppings.length > 0 ? leftToppings.join(", ") : "Plain";
      customizations.right_half = rightToppings.length > 0 ? rightToppings.join(", ") : "Plain";
    } else {
      customizations.toppings = wholeToppings.length > 0 ? wholeToppings.join(", ") : "None";
    }

    // Add premium toppings info if any selected
    const allSelected = halfAndHalf ? [...leftToppings, ...rightToppings] : wholeToppings;
    const selectedPremium = allSelected.filter(t => 
      (toppings.premium || []).find(p => (typeof p === 'string' ? p : p.name) === t)
    );
    if (selectedPremium.length > 0 && totalPremiumCost > 0) {
      customizations.premium_toppings = selectedPremium.join(", ") + ` (+$${totalPremiumCost.toFixed(2)})`;
    }

    let name = pizzaItem?.name || "Custom Pizza";
    if (isLimitedEdition) name += " (Limited Edition)";
    if (halfAndHalf) name += " (Half & Half)";

    addItem({
      name,
      concept: "pizza",
      category: isSpecialty ? "specialty_pizza" : (pizzaItem?.category || "custom_pizza"),
      price: totalPrice,
      quantity: 1,
      customizations,
    });
    toast.success(`${name} added to cart`);
    handleClose();
  };

  if (loadingCrust) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-slate-200 border-t-emerald-500 rounded-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If no crusts are available for this pizza type
  if (availableCrusts.length === 0) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle>Crust Unavailable</DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
            <p className="text-slate-600">
              {isSpecialty 
                ? "Regular crust is currently unavailable. Specialty pizzas require regular crust."
                : "No pizza crusts are currently available. Please check back later."}
            </p>
          </div>
          <Button variant="outline" className="w-full" onClick={handleClose}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle style={{ fontFamily: "Manrope" }} className="text-lg">
            {pizzaItem?.name || "Build Your Pizza"}
            {isSpecialty && (
              <span className="ml-2 text-sm font-normal text-emerald-600">$12.99</span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSpecialty ? "Regular Crust Only" : "Customize your pizza"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          {/* Crust Selection */}
          <h4 className="font-bold text-sm text-slate-900 mb-2 mt-2" style={{ fontFamily: "Manrope" }}>
            Crust 
            {availableCrusts.length === 1 && (
              <span className="text-xs text-slate-500 font-normal ml-2">
                ({availableCrusts[0].name} only)
              </span>
            )}
          </h4>
          
          {/* Show warning if only one crust is available (not specialty) */}
          {!isSpecialty && availableCrusts.length === 1 && (
            <div className="mb-3 p-2 rounded-lg flex items-center gap-2" style={{ backgroundColor: "#FEF3C7" }}>
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-xs text-amber-800">
                {!crustAvailability.regular ? "Regular crust" : "Thin crust"} currently unavailable
              </span>
            </div>
          )}
          
          <div className="space-y-2">
            {availableCrusts.map((c) => (
              <button key={c.id} data-testid={`crust-${c.id}`}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center justify-between ${selectedCrust === c.name ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                onClick={() => setSelectedCrust(c.name)}
              >
                <p className="font-semibold text-sm text-slate-900">{c.name}</p>
                {selectedCrust === c.name && <Check className="h-4 w-4 text-emerald-500" />}
              </button>
            ))}
          </div>

          {/* Extra Cheese */}
          <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between">
            <div>
              <p className="font-bold text-sm text-slate-900" style={{ fontFamily: "Manrope" }}>Extra Cheese</p>
              <p className="text-xs text-slate-500">+{formatPrice(extraCheesePrice)}</p>
            </div>
            <Switch data-testid="extra-cheese-toggle" checked={extraCheese} onCheckedChange={setExtraCheese} />
          </div>

          {/* Half & Half Toggle — available for all pizzas including specialty (Phase 15.4).
              Turning it on for a specialty seeds each half with the specialty's preset; the
              user can then customize either side. */}
          <div className="mt-4 p-3 rounded-xl bg-emerald-50 border-2 border-dashed border-emerald-300 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-emerald-600" />
              <div>
                <p className="font-bold text-sm text-slate-900" style={{ fontFamily: "Manrope" }}>Half & Half</p>
                <p className="text-xs text-slate-500">Different toppings each half</p>
              </div>
            </div>
            <Switch
              data-testid="half-and-half-toggle"
              checked={halfAndHalf}
              onCheckedChange={(v) => {
                setHalfAndHalf(v);
                if (v) {
                  // Seed each half from the current whole selection (or specialty preset)
                  const seed = wholeToppings.length ? wholeToppings : getPresetFor(pizzaItem?.id);
                  setLeftToppings(seed);
                  setRightToppings(seed);
                } else {
                  // Falling back to whole — keep whatever whole selection existed
                  if (wholeToppings.length === 0 && (leftToppings.length || rightToppings.length)) {
                    setWholeToppings(leftToppings.length ? leftToppings : rightToppings);
                  }
                }
              }}
            />
          </div>

          {/* Toppings — always shown so specialty pizzas can be tweaked (Phase 15.4) */}
          <>
              <h4 className="font-bold text-sm text-slate-900 mb-2 mt-4" style={{ fontFamily: "Manrope" }}>
                Toppings
                {hasPreset && (
                  <span className="ml-2 text-[10px] font-normal text-emerald-700" data-testid="specialty-preset-hint">
                    (preset selected — tap to deselect)
                  </span>
                )}
              </h4>

              {halfAndHalf ? (
                <Tabs defaultValue="left" className="mt-2">
                  <TabsList className="w-full h-9 bg-slate-100">
                    <TabsTrigger value="left" className="flex-1 text-xs font-semibold" data-testid="tab-left-half">Left Half</TabsTrigger>
                    <TabsTrigger value="right" className="flex-1 text-xs font-semibold" data-testid="tab-right-half">Right Half</TabsTrigger>
                  </TabsList>
                  <TabsContent value="left">
                    <ToppingChecklist toppingsData={displayToppings} selected={leftToppings} onToggle={toggleTopping} side="left" />
                  </TabsContent>
                  <TabsContent value="right">
                    <ToppingChecklist toppingsData={displayToppings} selected={rightToppings} onToggle={toggleTopping} side="right" />
                  </TabsContent>
                </Tabs>
              ) : (
                <ToppingChecklist toppingsData={displayToppings} selected={wholeToppings} onToggle={toggleTopping} side="whole" />
              )}
            </>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 border-t border-slate-100">
          {totalPremiumCost > 0 && (
            <p className="text-xs text-center mb-2" style={{ color: "#B45309" }}>
              Premium toppings: +${totalPremiumCost.toFixed(2)}
            </p>
          )}
          <Button data-testid="pizza-confirm-btn" className="w-full h-12 rounded-xl font-bold" style={{ backgroundColor: "#10B981" }}
            disabled={!selectedCrust} onClick={handleConfirm}>
            Add to Cart - {formatPrice(totalPrice)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
